import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AppDispatch, RootState } from '../../../store/store';
import { clearSelectedEnrollment, fetchEnrollmentDetail } from '../store/enrollmentsSlice';
import { cancelEnrollment as apiCancel, openDispute as apiDispute } from '../api/escrow';
import SchedulePanel from '../components/SchedulePanel';
import DisputeDialog from '../components/DisputeDialog';
import EnrollmentReceipt from '../components/EnrollmentReceipt';
import SatisfactionSurvey from '../components/SatisfactionSurvey';
import {
  ENROLLMENT_STATE_LABEL,
  formatDeadline,
  REQUEST_OUTCOME_COPY,
} from '../components/enrollmentRequest';
import { formatMoney } from '../../../commodity/format/money';
import { closingLabel, closingLabelInline, profileOf } from '../vocabulary/learningVocabulary';

const EnrollmentDetail: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { selectedEnrollment, loading, error } = useSelector(
    (state: RootState) => state.enrollments,
  );
  const userRole = useSelector((state: RootState) => (state.auth as any).user?.role as string);
  const userId = useSelector((state: RootState) => (state.auth as any).user?.sub as string);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);

  useEffect(() => {
    if (enrollmentId) {
      dispatch(fetchEnrollmentDetail(enrollmentId));
    }
    return () => {
      dispatch(clearSelectedEnrollment());
    };
  }, [dispatch, enrollmentId]);

  const isTeacher = useMemo(
    () => selectedEnrollment?.enrollment.teacherId === userId || userRole === 'TEACHER',
    [selectedEnrollment, userId, userRole],
  );
  const isParent = useMemo(
    () => selectedEnrollment?.enrollment.parentId === userId || userRole === 'PARENT',
    [selectedEnrollment, userId, userRole],
  );

  const refresh = async () => {
    if (enrollmentId) await dispatch(fetchEnrollmentDetail(enrollmentId));
  };

  // Which milestone the dispute dialog is open for, if any.
  const [disputeSequence, setDisputeSequence] = useState<number | null>(null);

  const handleDispute = async (sequence: number, reason: string) => {
    if (!enrollmentId) return;
    setBusy(true);
    setActionError(null);
    setActionInfo(null);
    try {
      await apiDispute(enrollmentId, sequence, reason);
      setDisputeSequence(null);
      setActionInfo(
        `Reportamos un problema con ${
          sequence === 8
            ? closingLabelInline(profileOf(enrollment?.learningProfile))
            : `la sesión ${sequence}`
        }. Ese pago queda retenido mientras lo revisamos.`,
      );
      await refresh();
    } catch (err: any) {
      setActionError(err.message || 'Error al reportar el problema');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!enrollmentId) return;
    const reason = window.prompt('Razon de la cancelacion:');
    if (!reason || reason.trim().length === 0) return;
    setBusy(true);
    setActionError(null);
    setActionInfo(null);
    try {
      const result = await apiCancel(enrollmentId, reason.trim());
      // ADR-005: a refund the provider refused leaves the enrollment as it was.
      if (result.refundOk === false) {
        setActionError(
          'No pudimos procesar tu reembolso, así que la inscripción sigue activa. Inténtalo de nuevo.',
        );
        await refresh();
        return;
      }
      const parts = [
        `Inscripcion cancelada.`,
        `Reembolso al parent: ${formatMoney(result.refundToParent, enrollment?.currency)}`,
        result.penaltyApplied
          ? `Penalty del 50% aplicada (${formatMoney(
              result.releaseToTeacher,
              enrollment?.currency,
            )})`
          : '',
      ].filter(Boolean);
      setActionInfo(parts.join(' '));
      await refresh();
    } catch (err: any) {
      setActionError(err.message || 'Error al cancelar inscripcion');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p>Cargando inscripcion...</p>;
  if (error) return <p style={{ color: '#e03131' }}>{error}</p>;
  if (!selectedEnrollment) return <p>Inscripcion no encontrada.</p>;

  const { enrollment, milestones } = selectedEnrollment;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/courses')}
        style={{
          background: 'none',
          border: 'none',
          color: '#0366d6',
          cursor: 'pointer',
          marginBottom: 16,
        }}
      >
        &larr; Volver a cursos
      </button>

      <h1>Inscripcion {enrollment.enrollmentId.slice(0, 8)}</h1>
      <p>
        Estado: <strong>{ENROLLMENT_STATE_LABEL[enrollment.state] ?? enrollment.state}</strong>
      </p>
      {/* D-P07-03 (CTO, 12 Oct 2026): authorized is not charged — say which. */}
      {enrollment.state === 'PendingTeacherAcceptance' && (
        <div role="status" style={gateBannerStyle('#e7f5ff', '#74c0fc')}>
          <p style={{ margin: 0 }}>{REQUEST_OUTCOME_COPY.awaiting}</p>
          <p style={{ margin: '6px 0 0' }}>
            El profesor tiene hasta el <strong>{formatDeadline(enrollment.acceptBy)}</strong> para
            responder.
          </p>
        </div>
      )}
      {(enrollment.state === 'TeacherDeclined' || enrollment.state === 'AcceptanceExpired') && (
        <div role="status" style={gateBannerStyle('#f8f9fa', '#ced4da')}>
          {enrollment.state === 'TeacherDeclined'
            ? REQUEST_OUTCOME_COPY.declined
            : REQUEST_OUTCOME_COPY.expired}
        </div>
      )}
      <p>
        Total: {formatMoney(enrollment.total, enrollment.currency)} | Booking fee:{' '}
        {enrollment.bookingFeeReleased ? '✅ liberado' : 'pendiente'} | Holdback:{' '}
        {enrollment.holdbackReleased ? '✅ liberado' : 'pendiente'} | Reembolsado:{' '}
        {formatMoney(enrollment.refundedAmount || 0, enrollment.currency)}
      </p>
      {enrollment.cancelledAt && (
        <p style={{ color: '#c92a2a' }}>
          Cancelada {new Date(enrollment.cancelledAt).toLocaleString()} —{' '}
          {enrollment.cancellationReason}
        </p>
      )}

      {actionError && (
        <div
          style={{
            background: '#ffe3e3',
            border: '1px solid #ff8787',
            borderRadius: 8,
            padding: 12,
            margin: '12px 0',
            color: '#c92a2a',
          }}
        >
          {actionError}
        </div>
      )}
      {actionInfo && (
        <div
          style={{
            background: '#d3f9d8',
            border: '1px solid #51cf66',
            borderRadius: 8,
            padding: 12,
            margin: '12px 0',
          }}
        >
          {actionInfo}
        </div>
      )}

      <h2>Milestones</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #d1d5da', textAlign: 'left' }}>
            <th style={{ padding: 8 }}>#</th>
            <th>Tipo</th>
            <th style={{ textAlign: 'right' }}>Monto</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {milestones.map((m) => (
            <tr key={m.milestoneId} style={{ borderBottom: '1px solid #e9ecef' }}>
              <td style={{ padding: 8 }}>{m.sequence}</td>
              <td>
                {m.type === 'final_project'
                  ? closingLabel(profileOf(enrollment?.learningProfile))
                  : 'Sesion'}
              </td>
              <td style={{ textAlign: 'right' }}>{formatMoney(m.amount, enrollment?.currency)}</td>
              <td>{m.status}</td>
              <td>
                {(isTeacher || isParent) &&
                  enrollment.state === 'Active' &&
                  m.type === 'session' &&
                  m.bookingId &&
                  m.status !== 'released' && (
                    <button
                      onClick={() => navigate(`/sessions/${m.bookingId}`)}
                      style={{ ...btnStyle(false), backgroundColor: '#2b8a3e', marginRight: 8 }}
                    >
                      {isTeacher ? 'Iniciar/Unirse' : 'Unirse'}
                    </button>
                  )}
                {/*
                  Evidence, not a bare "mark complete": ADR-005 will not release
                  this milestone's escrow without a recording, check-in, minimum
                  duration and rubric, so the teacher is sent where that gets
                  recorded rather than to a button that now refuses.
                */}
                {isTeacher && enrollment.state === 'Active' && m.status !== 'released' && (
                  <button
                    onClick={() =>
                      navigate(`/enrollments/${enrollment.enrollmentId}/sessions/${m.sequence}`)
                    }
                    disabled={busy}
                    style={btnStyle(busy)}
                  >
                    Registrar evidencia
                  </button>
                )}
                {isParent && (m.status === 'completed' || m.status === 'released') && (
                  <button
                    onClick={() =>
                      navigate(`/enrollments/${enrollment.enrollmentId}/sessions/${m.sequence}`)
                    }
                    style={{ ...btnStyle(false), background: '#495057', marginRight: 8 }}
                  >
                    Ver evaluación
                  </button>
                )}
                {isParent &&
                  enrollment.state === 'Active' &&
                  (m.status === 'pending' || m.status === 'completed') && (
                    <button
                      onClick={() => setDisputeSequence(m.sequence)}
                      disabled={busy}
                      style={{ ...btnStyle(busy), backgroundColor: '#c92a2a', marginLeft: 8 }}
                    >
                      Reportar problema
                    </button>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SchedulePanel
        enrollmentId={enrollment.enrollmentId}
        enrollmentState={enrollment.state}
        isParent={isParent}
        isTeacher={isTeacher}
      />

      {/* PRT-07-FR-12 — the family's receipt. The server serves it to the
          parent of record only; the teacher reads the same money in their
          ledger. Keyed on updatedAt so a cancellation's refund appears
          without a reload. */}
      {isParent && !isTeacher && (
        <EnrollmentReceipt
          enrollmentId={enrollment.enrollmentId}
          refreshKey={enrollment.updatedAt}
        />
      )}

      {/* MC-06 §6 — the private question, once the cycle has ended. The
          server decides when it is available and refuses anyone but the
          parent of record. */}
      {isParent && !isTeacher && <SatisfactionSurvey enrollmentId={enrollment.enrollmentId} />}

      {isParent && enrollment.state === 'Active' && (
        <button
          onClick={handleCancel}
          disabled={busy}
          style={{
            marginTop: 24,
            padding: 12,
            border: '1px solid #c92a2a',
            background: '#fff',
            color: '#c92a2a',
            borderRadius: 6,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          Cancelar inscripcion
        </button>
      )}

      {disputeSequence !== null && (
        <DisputeDialog
          sequence={disputeSequence}
          busy={busy}
          onCancel={() => setDisputeSequence(null)}
          onConfirm={(reason) => handleDispute(disputeSequence, reason)}
        />
      )}
    </div>
  );
};

const btnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  border: 'none',
  borderRadius: 4,
  background: disabled ? '#a0c4e8' : '#1971c2',
  color: '#fff',
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const gateBannerStyle = (background: string, border: string): React.CSSProperties => ({
  background,
  border: `1px solid ${border}`,
  borderRadius: 8,
  padding: 12,
  margin: '12px 0',
});

export default EnrollmentDetail;
